import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const PAGE_W_MM = 210;
const PAGE_H_MM = 297;
const MARGIN_MM = 10;
/** Minimal vertical gap between rasterized sections on the same PDF page (mm). */
const GAP_MM = 1;
/** Desktop-width capture so PDF layout matches full plan, not mobile viewport. */
const PLAN_PDF_CAPTURE_WIDTH_PX = 1200;
const H2C_OPTS = {
  scale: 2,
  useCORS: true,
  allowTaint: true,
  logging: false,
  backgroundColor: "#ffffff",
};

/**
 * Width (CSS px) for html2canvas — at least desktop min width, or wider if layout already is.
 */
function planContentCaptureWidth(root) {
  if (!(root instanceof HTMLElement)) {
    return PLAN_PDF_CAPTURE_WIDTH_PX;
  }
  const rect = root.getBoundingClientRect();
  const elementW = Math.max(
    root.offsetWidth || 0,
    root.clientWidth || 0,
    Math.ceil(rect.width) || 0,
    1
  );
  return Math.max(PLAN_PDF_CAPTURE_WIDTH_PX, Math.ceil(elementW) + 2);
}

function captureWindowHeight(el) {
  return Math.max(
    el.scrollHeight,
    el.offsetHeight,
    el.getBoundingClientRect().height,
    1
  );
}

function sectionLayoutHeightPx(el) {
  return Math.max(
    el.offsetHeight,
    el.getBoundingClientRect().height,
    el.scrollHeight,
    1
  );
}

/** Show all coaching copy in the clone (web UI may collapse sections). */
function expandCoachingInPdfClone(weekCard) {
  if (!(weekCard instanceof HTMLElement)) return;
  weekCard
    .querySelectorAll(".pw-coaching-hidden-when-collapsed")
    .forEach((node) => {
      node.classList.remove("pw-coaching-hidden-when-collapsed");
    });
  weekCard.querySelectorAll(".pw-coaching-toggle").forEach((node) => {
    node.style.display = "none";
  });
}

/** Stack week title, workouts, then full-width coaching for PDF (matches in-card source order). */
function stackWeekCardLayoutForPdf(weekCard) {
  if (!(weekCard instanceof HTMLElement)) return;
  const body = weekCard.querySelector(".pw-plan-week-body");
  const title = weekCard.querySelector(".pw-plan-week-title");
  const mainCol =
    weekCard.querySelector(".pw-plan-week-main-wrap") ||
    weekCard.querySelector(".pw-plan-week-main");
  const aside = weekCard.querySelector("aside.pw-plan-week-coaching");
  if (!body || !mainCol) return;

  body.style.display = "flex";
  body.style.flexDirection = "column";
  body.style.alignItems = "stretch";
  body.style.gap = "1.25rem";

  if (title) {
    if (title.parentNode !== body) {
      body.insertBefore(title, body.firstChild);
    } else if (body.firstChild !== title) {
      body.insertBefore(title, body.firstChild);
    }
  }
  body.appendChild(mainCol);
  if (aside) {
    body.appendChild(aside);
    aside.style.width = "100%";
    aside.style.maxWidth = "none";
    aside.style.boxSizing = "border-box";
    aside.style.borderRadius = "10px";
  }
}

/**
 * Mean height of non–race week cards (for matching race week capture size in PDF).
 */
function averagePeerWeekHeightPx(sections) {
  const weeks = sections.filter(
    (s) => s instanceof HTMLElement && s.getAttribute("data-pw-plan-section") === "week"
  );
  const peerHeights = weeks
    .filter((s) => !s.hasAttribute("data-pw-race-week"))
    .map(sectionLayoutHeightPx);
  if (peerHeights.length > 0) {
    return peerHeights.reduce((a, b) => a + b, 0) / peerHeights.length;
  }
  return 0;
}

/**
 * html2canvas sometimes returns a bitmap narrower than `captureW * scale` for very short sections
 * (e.g. race week), which makes that week look smaller and horizontally offset in the PDF.
 * Resample to a fixed width so every section maps to the same PDF width.
 */
function normalizeCanvasToRefPixelWidth(source, refPixelWidth) {
  const w = source.width;
  const ref = Math.max(1, Math.round(refPixelWidth));
  if (w === ref) return source;
  const h = Math.max(1, Math.round((source.height * ref) / w));
  const out = document.createElement("canvas");
  out.width = ref;
  out.height = h;
  const ctx = out.getContext("2d");
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source, 0, 0, ref, h);
  }
  return out;
}

/**
 * Safe filename: "{RaceDistance}-{YYYY-MM-DD}.pdf"
 */
export function buildPlanPdfFilename(formData) {
  const rawDist = String(formData?.raceDistance ?? "Training-Plan").trim() || "Training-Plan";
  const safeDist = rawDist
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

  let datePart = "";
  const rd = formData?.raceDate;
  if (rd && typeof rd === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(rd);
    if (m) datePart = `${m[1]}-${m[2]}-${m[3]}`;
    else datePart = rd.slice(0, 10).replace(/[^\d-]/g, "") || "";
  }
  if (!datePart) datePart = "date-unknown";

  return `${safeDist}-${datePart}.pdf`;
}

/**
 * Rasterize each direct child of `#plan-content` (summary + week cards; skips view toggles).
 * Pack as many sections as fit per A4 page (every sheet uses full A4 height). Race week clones use
 * minHeight ≈ average of other week cards so html2canvas captures a similar viewport to peer weeks.
 */
export async function buildPlanPdfDocument(element, options = {}) {
  if (!element || !(element instanceof HTMLElement)) {
    throw new Error("Missing plan element to export");
  }

  const innerW = PAGE_W_MM - 2 * MARGIN_MM;
  const maxContentH = PAGE_H_MM - 2 * MARGIN_MM;
  const captureW = planContentCaptureWidth(element);
  const captureScale = options.scale ?? H2C_OPTS.scale ?? 2;
  const jpegQuality = options.jpegQuality;
  const useJpeg = jpegQuality != null && jpegQuality < 1;
  const scale = captureScale;
  /** Expected raster width for a full-width section; used to normalize odd html2canvas widths. */
  const refPixelW = Math.max(1, Math.round(captureW * scale));

  let sections = Array.from(element.children).filter(
    (n) =>
      n instanceof HTMLElement &&
      !n.classList.contains("pw-plan-view-controls") &&
      !n.hasAttribute("data-pw-exclude-pdf")
  );
  if (sections.length === 0) {
    sections = [element];
  }

  const avgPeerWeekH = averagePeerWeekHeightPx(sections);
  const raceWeekTargetH =
    avgPeerWeekH > 0 ? Math.ceil(avgPeerWeekH) : 360;

  const items = [];
  for (const el of sections) {
    const isRaceWeekSection =
      el instanceof HTMLElement &&
      el.getAttribute("data-pw-plan-section") === "week" &&
      el.hasAttribute("data-pw-race-week");
    const layoutH = captureWindowHeight(el);
    const minHForCapture = isRaceWeekSection ? Math.max(layoutH, raceWeekTargetH) : layoutH;

    const isWeekSection =
      el instanceof HTMLElement &&
      el.getAttribute("data-pw-plan-section") === "week";
    const hasCoachingAside =
      isWeekSection && el.querySelector("aside.pw-plan-week-coaching");
    const coachingWasCollapsed =
      isWeekSection &&
      el.querySelector(".pw-coaching-hidden-when-collapsed");
    /**
     * PDF stacks coaching under workouts (taller than the 2-column web layout) and may expand
     * collapsed sections — capture viewport must allow the full stacked height.
     */
    const pdfCoachingBuffer = !hasCoachingAside
      ? 0
      : coachingWasCollapsed
        ? Math.max(900, Math.ceil(layoutH * 1.25))
        : Math.max(350, Math.ceil(layoutH * 0.55));
    const windowHeightPx =
      Math.ceil(minHForCapture) + 16 + pdfCoachingBuffer;

    const rawCanvas = await html2canvas(el, {
      ...H2C_OPTS,
      scale: captureScale,
      windowWidth: captureW,
      // Race week: tall enough viewport + clone minHeight so capture matches peer week cards.
      windowHeight: windowHeightPx,
      onclone: (clonedDoc, clonedEl) => {
        clonedDoc.querySelectorAll("[data-pw-exclude-pdf]").forEach((node) => {
          if (node instanceof HTMLElement) {
            node.style.display = "none";
          }
        });
        if (!(clonedEl instanceof HTMLElement)) return;
        const w = `${captureW}px`;
        clonedEl.style.width = w;
        clonedEl.style.minWidth = w;
        clonedEl.style.maxWidth = w;
        clonedEl.style.boxSizing = "border-box";
        clonedEl.style.display = "block";
        clonedEl.style.marginLeft = "0";
        clonedEl.style.marginRight = "0";
        if (clonedEl.getAttribute("data-pw-plan-section") === "week") {
          expandCoachingInPdfClone(clonedEl);
          stackWeekCardLayoutForPdf(clonedEl);
          const sh = clonedEl.scrollHeight;
          if (sh > 0) {
            clonedEl.style.minHeight = `${sh}px`;
          }
        }
        if (
          clonedEl.getAttribute("data-pw-plan-section") === "week" &&
          clonedEl.hasAttribute("data-pw-race-week") &&
          raceWeekTargetH > 0
        ) {
          clonedEl.style.minHeight = `${raceWeekTargetH}px`;
        }
        // White background theme for PDF
        clonedEl.style.backgroundColor = "#ffffff";
        clonedEl.style.color = "#111111";
        clonedEl.querySelectorAll("*").forEach((node) => {
          if (node instanceof HTMLElement) {
            const bg = window.getComputedStyle(node).backgroundColor;
            if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
              node.style.backgroundColor = "#f5f5f5";
            }
            node.style.color = "#111111";
          }
        });

        clonedEl
          .querySelectorAll(
            ".pw-workout-line--xtraining .pw-workout-day-label, " +
              ".pw-workout-line--xtraining .pw-workout-line-text, " +
              ".pw-plan-cal-cell--xtraining .pw-plan-cal-type, " +
              ".pw-plan-cal-cell--xtraining .pw-plan-cal-detail"
          )
          .forEach((node) => {
            if (node instanceof HTMLElement) {
              node.style.color = "#a78bfa";
            }
          });

        if (clonedEl.getAttribute("data-pw-plan-section") === "summary") {
          clonedEl.style.background = "#ffffff";
          clonedEl.style.backgroundImage = "none";
          clonedEl.style.border = "1px solid #ddd";

          clonedEl.querySelectorAll(".pw-summary-stat").forEach((stat) => {
            if (stat instanceof HTMLElement) {
              stat.style.background = "#f0f0f0";
              stat.style.backgroundImage = "none";
              stat.style.color = "#111111";
            }
          });

          clonedEl.querySelectorAll("*").forEach((node) => {
            if (!(node instanceof HTMLElement)) return;
            const bg = window.getComputedStyle(node).backgroundColor;
            if (
              bg &&
              (bg.includes("rgba(0, 0, 0") || bg.includes("rgba(0,0,0"))
            ) {
              node.style.background = "#f0f0f0";
              node.style.backgroundImage = "none";
            }
            node.style.color = "#111111";
          });
        }
      },
    });

    const canvas = normalizeCanvasToRefPixelWidth(rawCanvas, refPixelW);
    const cw = Math.max(canvas.width, 1);
    const ch = Math.max(canvas.height, 1);
    const imageFormat = useJpeg ? "JPEG" : "PNG";
    const imgData = useJpeg
      ? canvas.toDataURL("image/jpeg", jpegQuality)
      : canvas.toDataURL("image/png");
    const aspect = cw / ch;
    let drawW = innerW;
    let drawH = innerW / aspect;
    if (drawH > maxContentH) {
      drawH = maxContentH;
      drawW = maxContentH * aspect;
    }
    const xOff = MARGIN_MM + (innerW - drawW) / 2;
    items.push({ imgData, drawW, drawH, xOff, imageFormat });
  }

  /** @type {{ items: typeof items }} */
  const pages = [];
  let current = { items: [] };
  let usedH = 0;

  for (const it of items) {
    const gap = current.items.length > 0 ? GAP_MM : 0;
    const nextH = usedH + gap + it.drawH;
    if (nextH > maxContentH && current.items.length > 0) {
      pages.push(current);
      current = { items: [it] };
      usedH = it.drawH;
    } else {
      current.items.push(it);
      usedH = nextH;
    }
  }
  if (current.items.length > 0) {
    pages.push(current);
  }

  let pdf = null;

  for (let pi = 0; pi < pages.length; pi++) {
    const pg = pages[pi];

    const pageH = PAGE_H_MM;

    if (pi === 0) {
      pdf = new jsPDF({
        unit: "mm",
        orientation: "p",
        format: [PAGE_W_MM, pageH],
      });
    } else {
      pdf.addPage([PAGE_W_MM, pageH]);
    }

    let y = MARGIN_MM;
    for (let j = 0; j < pg.items.length; j++) {
      const it = pg.items[j];
      if (j > 0) y += GAP_MM;
      pdf.addImage(
        it.imgData,
        it.imageFormat || "PNG",
        it.xOff,
        y,
        it.drawW,
        it.drawH
      );
      y += it.drawH;
    }
  }

  if (!pdf) {
    throw new Error("Failed to build PDF");
  }
  return pdf;
}

export async function exportPlanElementToPdfBlob(element, options) {
  const pdf = await buildPlanPdfDocument(element, options);
  return pdf.output("blob");
}

export async function exportPlanElementToPdf(element, filename) {
  const pdf = await buildPlanPdfDocument(element);
  pdf.save(filename);
}
